import { useState, type PropsWithChildren } from "react";

import type { Project } from "@tudigo-monorepo/core-tudigo-api-models";
import { cn } from "@tudigo-monorepo/core-tudigo-theme";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { useGetProjectsQuery } from "@tudigo-monorepo/web-tudigo-api-client";
import { CarouselMedia } from "@tudigo-monorepo/web-tudigo-components";

import {
  ProjectCardAvatar,
  ProjectCardBadges,
  ProjectCardHeader,
  ProjectCardLabel,
} from "../../../../shared-external/components/project-card/project-card";
import { ProjectCardProvider } from "../../../../shared-external/components/project-card/store/project-card.context";
import { ProjectMechanicSettingsCard } from "../../../shared-internal/components/project-mechanic-settings-card";
import {
  MediaSlider,
  MediaSliderContent,
  MediaSliderControls,
  MediaSliderItem,
} from "../../../shared-internal/components/search-projects/media-slider/media-slider";
import { MediaSliderProvider } from "../../../shared-internal/components/search-projects/media-slider/media-slider.context";
import { SCHEDULED_PROJECTS_LIMIT } from "../default-started-projects-layout";
import { ProjectNotify } from "../project-notify/project-notify";
import { ScheduledProjectsSliderSkeleton } from "./scheduled-projects-slider.skeleton";

function ProjectSlideContentDetails(props: { project: Project }) {
  return (
    <ProjectCardProvider project={props.project}>
      <div className="flex w-full flex-col gap-4 xl:items-start">
        <ProjectCardHeader className="flex text-left">
          <ProjectCardAvatar className="h-[40px] w-[40px]" />
          <div className="flex flex-col gap-2">
            <ProjectCardLabel />
          </div>
        </ProjectCardHeader>

        <ProjectCardBadges />
        <p className="text-black-900 line-clamp-6 block max-h-[40px] min-h-[35px] w-[325px] overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-tight sm:w-[325px] md:w-[600px] xl:w-[400px] 2xl:w-[655px]">
          {props.project.content?.shortDescription}
        </p>

        <ProjectMechanicSettingsCard
          className="mb-0 xl:min-h-[132px]"
          project={props.project}
        />
      </div>
    </ProjectCardProvider>
  );
}

function ProjectSlideContent(props: PropsWithChildren<{ project: Project }>) {
  const projectMedia = props.project.content?.medias?.[0];

  return (
    <article className="flex h-full w-full flex-col items-start gap-6 rounded-md bg-white xl:flex-row-reverse xl:p-4 2xl:flex-row-reverse">
      {projectMedia ? (
        <CarouselMedia
          media={projectMedia}
          className="w-full xl:h-full xl:max-h-full xl:min-h-full xl:min-w-[664px] xl:max-w-[664px] 2xl:min-w-[464px] 2xl:max-w-[664px]"
        />
      ) : (
        <div className="h-[240px] w-full xl:h-full xl:w-[364px] 2xl:w-[664px]" />
      )}
      <div className="flex w-full flex-col items-center px-4 pb-4 xl:h-full xl:w-full xl:items-start">
        <ProjectSlideContentDetails project={props.project} />
        <ProjectNotify
          project={props.project}
          className="mt-4 w-full xl:mt-6"
        />
      </div>
    </article>
  );
}

export function ScheduledProjectsSlider() {
  const { t } = useTranslation();

  const { data: projects, isFetching } = useGetProjectsQuery({
    filters: { status: "scheduled", isReferenced: true },
    fields:
      "id,risk_in_percent,impact_score_in_percent,shares_mechanic_settings,bonds_mechanic_settings,tax_exemption_mechanics,total_amount_collected_in_cents,fundraising_progress_min_goal,categories,mechanic,tags,finished_at,marketing_settings,scheduled_end_date,status,goal,maturity,is_referenced,informations,content.fields(name,short_description,slug,cover,logo,medias),club.fields(id,icon_border_color,content.fields(logo)),is_trending",
    pagination: { limit: SCHEDULED_PROJECTS_LIMIT },
  });

  const scheduledProjects =
    projects?.data.filter((project) => !!project.content?.medias?.length) ?? [];

  const [currentSlide, setCurrentSlide] = useState(0);

  if (scheduledProjects.length === 0) {
    return null;
  }

  return (
    <MediaSliderProvider
      defaultSlide={currentSlide}
      onSlideChange={setCurrentSlide}
    >
      <section
        className={cn(
          "bg-accent-super-light border-accent-light font-roobert flex min-h-[970px] flex-col gap-y-6 border md:min-h-[1050px] lg:min-h-[1175px] xl:min-h-[1050px] xl:min-h-[630px] 2xl:min-h-[630px]",
          "w-screen sm:w-full",
          "my-12 -ml-4 sm:ml-0",
          "px-4 py-6 md:p-10",
          "sm:rounded-xl",
        )}
      >
        {isFetching ? (
          <ScheduledProjectsSliderSkeleton />
        ) : (
          <MediaSlider
            className="flex flex-col"
            onSlideChange={setCurrentSlide}
            autoplay
          >
            <header className="flex justify-between xl:items-end">
              <div className="flex flex-col items-center justify-center gap-2 text-center xl:items-start xl:text-left">
                <h1 className="text-black-primary text-2xl font-semibold">
                  {t(
                    "search_projects_page.started.scheduled_projects_carousel.title",
                  )}
                </h1>
                <p className="text-black-900 w-full text-sm md:w-2/3 xl:text-pretty">
                  {t(
                    "search_projects_page.started.scheduled_projects_carousel.description",
                  )}
                </p>
              </div>

              <MediaSliderControls className="hidden xl:flex 2xl:flex" />
            </header>
            <MediaSliderContent className="min-h-[861px] xl:min-h-[461px]">
              {scheduledProjects.map((project, index) => (
                <MediaSliderItem key={project.id} slide={index}>
                  <ProjectSlideContent project={project} />
                </MediaSliderItem>
              ))}
            </MediaSliderContent>
            <MediaSliderControls className="relative top-[650px] flex justify-between md:top-[785px] lg:top-[920px] xl:hidden 2xl:hidden" />
          </MediaSlider>
        )}
      </section>
    </MediaSliderProvider>
  );
}
